import { LegalLayout, LegalSection } from './LegalLayout';

export default function DataDeletion() {
  return (
    <LegalLayout
      title="Eliminación de Datos"
      metaDescription="Cómo solicitar la eliminación de tus datos o de tu cuenta en FinScope: procedimiento, qué datos se eliminan y qué información puede conservarse por obligación legal."
      lastUpdated="28 de junio de 2026"
    >
      <p className="text-muted-foreground">
        Esta página describe cómo solicitar la eliminación de tus datos personales y/o de tu cuenta en
        FinScope, qué información puede eliminarse y qué información podría conservarse cuando exista una
        obligación legal o legítima para ello. Complementa lo descrito en nuestra{' '}
        <a href="/privacy-policy" className="text-primary hover:underline">Política de Privacidad</a>.
      </p>

      <LegalSection number={1} title="Cómo solicitar la eliminación">
        <p>Puedes solicitar la eliminación de tus datos o de tu cuenta de las siguientes formas:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <span className="font-medium text-foreground">A través del administrador de tu organización:</span>{' '}
            las cuentas de FinScope pertenecen a una organización; su administrador puede canalizar tu
            solicitud directamente con el equipo de FinScope.
          </li>
          <li>
            <span className="font-medium text-foreground">A través de los canales de atención de la plataforma:</span>{' '}
            cualquier usuario puede presentar la solicitud mediante los canales de contacto disponibles dentro
            de FinScope.
          </li>
        </ul>
        <p>
          Al presentar la solicitud, indica el correo electrónico asociado a tu cuenta y si deseas la
          eliminación de datos específicos, de tu cuenta de usuario o de toda la información de tu
          organización. Podremos verificar tu identidad antes de procesarla.
        </p>
      </LegalSection>

      <LegalSection number={2} title="Qué datos pueden eliminarse">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <span className="font-medium text-foreground">Datos de cuenta y perfil:</span> nombre, correo
            electrónico, rol y credenciales de acceso.
          </li>
          <li>
            <span className="font-medium text-foreground">Información financiera y de negocio:</span> los
            movimientos, categorías, clientes, facturación, pagos, prioridades y demás datos que tu
            organización haya cargado o gestionado en la plataforma (a solicitud del administrador de la
            organización, por ser información de titularidad de la organización).
          </li>
          <li>
            <span className="font-medium text-foreground">Credenciales de integraciones:</span> los tokens
            cifrados de servicios conectados, como Google.
          </li>
          <li>
            <span className="font-medium text-foreground">Configuraciones de la cuenta:</span> preferencias,
            configuraciones de sincronización y archivos seleccionados como fuente de datos.
          </li>
        </ul>
      </LegalSection>

      <LegalSection number={3} title="Eliminación de datos de Google">
        <p>
          Si conectaste una cuenta de Google, puedes eliminar el acceso de forma inmediata y sin necesidad de
          presentar una solicitud:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            Desde FinScope: en la sección <span className="font-medium text-foreground">Conexión de Datos</span>,
            utiliza la opción &quot;Desconectar&quot;. FinScope solicita a Google la revocación del token y
            elimina las credenciales cifradas almacenadas.
          </li>
          <li>
            Desde tu cuenta de Google: revoca el acceso de FinScope en{' '}
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              myaccount.google.com/permissions
            </a>
            .
          </li>
        </ul>
        <p>
          FinScope solo solicita permisos de solo lectura sobre Google Drive y Google Sheets; tus archivos de
          Google nunca se modifican ni se eliminan por FinScope. Los datos que se hayan importado a la
          plataforma a partir de esos archivos pueden eliminarse conforme a la sección 2.
        </p>
      </LegalSection>

      <LegalSection number={4} title="Información que podría conservarse">
        <p>
          Ciertos datos pueden conservarse temporalmente después de una solicitud de eliminación, únicamente
          cuando exista una razón legal o legítima, por ejemplo:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Registros necesarios para el cumplimiento de obligaciones legales, fiscales o contables aplicables.</li>
          <li>Registros técnicos de seguridad y auditoría, durante un periodo razonable, para prevenir fraudes o abusos.</li>
          <li>Información necesaria para la defensa ante reclamaciones o requerimientos de autoridad competente.</li>
          <li>Copias de respaldo, hasta que expiren conforme a los ciclos normales de respaldo de la infraestructura.</li>
        </ul>
        <p>
          La información conservada por estos motivos deja de utilizarse para cualquier otro fin y se elimina o
          anonimiza cuando la razón de conservación desaparece.
        </p>
      </LegalSection>

      <LegalSection number={5} title="Plazos">
        <p>
          Confirmaremos la recepción de tu solicitud y la atenderemos en un plazo razonable conforme a la
          legislación aplicable. Te notificaremos una vez completada la eliminación o, en su caso, la razón
          legítima por la que determinada información deba conservarse temporalmente.
        </p>
      </LegalSection>

      <LegalSection number={6} title="Contacto">
        <p>
          Para dudas sobre este procedimiento, contáctanos a través de los canales de atención disponibles
          dentro de la plataforma o mediante el administrador de la cuenta de tu organización.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
